'use client'

import Image from "next/image";
import Link from "@/components/ui/Link";
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";
import { useEffect, useRef, useState } from "react";

interface EducationQualificationLeadershipData {
  anchor_title?: string;
  heading?: {
    text?: string;
    title?: string;
  };
  image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        item: {
          title: string;
          text: string;
        };
      }[];
    };
  }[];
  images?: {
    images_id?: {
      alt?: string;
      image_file?: {
        image?: {
          id?: string;
          filename_download?: string;
        };
      };
    };
  }[];
  button?: {
    label?: string;
    href?: string;
  };
}

export const fields = [
  "anchor_title",
  "heading.text",
  "heading.title",
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download",
  "button.label",
  "button.href",
] as const;

export default function EducationQualificationLeadership({ data }: { data?: EducationQualificationLeadershipData }) {
  const sectionRef = useRef<HTMLElement>(null);
  const logosRef = useRef<HTMLDivElement>(null);
  const [sectionVisible, setSectionVisible] = useState(false);
  const [logosVisible, setLogosVisible] = useState(false);

  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      ([entry]) => setSectionVisible(entry.isIntersecting),
      { threshold: 0.08, rootMargin: "0px" }
    );
    const sectionCurrent = sectionRef.current;
    if (sectionCurrent) sectionObserver.observe(sectionCurrent);

    const observer = new IntersectionObserver(
      ([entry]) => setLogosVisible(entry.isIntersecting),
      { threshold: 0.15, rootMargin: "-50px" }
    );
    const current = logosRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
      if (sectionCurrent) sectionObserver.unobserve(sectionCurrent);
    };
  }, []);

  const imageUrl = getReadableImageUrl(
    data?.image?.image_file?.image?.id || "",
    data?.image?.image_file?.image?.filename_download || ""
  );

  const columns = data?.content_collection || [];

  const sectionId = (data?.anchor_title || 'EducationQualificationLeadership')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .replace(/-{2,}/g, '-');

  return (
    <section id={sectionId} ref={sectionRef} className='EducationQualificationLeadership py-16'>
      <div className='container'>
        {data?.heading && (
          <div
            className={`text-center mb-8 md:mb-12 transition-all duration-700 ease-out ${
              sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <h2 className='mb-0'>
              {data.heading.title || data.heading.text}
            </h2>
          </div>
        )}

        {data?.image && imageUrl && (
          <div
            className={`w-full overflow-hidden mb-10 md:mb-14 transition-all duration-700 ease-out ${
              sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: sectionVisible ? '80ms' : '0ms' }}
          >
            <Image
              src={imageUrl}
              alt={data.image.alt || ''}
              width={1200}
              height={500}
              quality={95}
              loading='lazy'
              className='w-full h-auto object-cover'
              sizes='100vw'
            />
          </div>
        )}

        {columns.length > 0 && (
          <div
            className={`grid md:grid-cols-3 gap-8 md:gap-12 mb-10 md:mb-14 transition-all duration-700 ease-out ${
              sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: sectionVisible ? '200ms' : '0ms' }}
          >
            {columns.map((col, index) => {
              const titleContent = col?.site_content_collections_id?.collection?.find(c => c.item.title)?.item.title;
              const textContent = col?.site_content_collections_id?.collection?.find(c => c.item.text)?.item.text;

              return (
                <div key={`col-${col.id}-${index}`}>
                  {titleContent && (
                    <h3 className='font-sans text-xl md:text-[1.75rem] font-semibold leading-8 md:leading-10 tracking-[0.07rem] uppercase text-black mb-4'>
                      {titleContent}
                    </h3>
                  )}
                  {textContent && (
                    <div
                      className='font-sans text-sm leading-7 text-black [&>ul]:list-disc [&>ul]:pl-5 [&>ul>li]:mb-1'
                      dangerouslySetInnerHTML={sanitizeHTML(textContent)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {data?.images && data.images.length > 0 && (
          <div
            ref={logosRef}
            className='py-6 border-y border-[#cfcfcf] grid grid-cols-6 gap-x-4 gap-y-6 md:flex md:flex-wrap items-center justify-center md:justify-between md:gap-8 2xl:gap-6'
          >
            {data.images.map((logo, index) => {
              const logoSrc = getReadableImageUrl(
                logo?.images_id?.image_file?.image?.id || '',
                logo?.images_id?.image_file?.image?.filename_download || '',
              )
              return (
              <div
                key={index}
                className={`col-span-2 transition-all duration-700 ease-out grayscale hover:grayscale-0 flex items-center justify-center ${
                  index === 3 ? 'col-start-2 md:col-start-auto' : ''
                } ${index === 4 ? 'col-start-4 md:col-start-auto' : ''} ${
                  logosVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                }`}
                style={{
                  transitionDelay: logosVisible ? `${index * 100}ms` : '0ms',
                }}
              >
                {logoSrc && (
                <Image
                  src={logoSrc}
                  alt={logo?.images_id?.alt || ''}
                  width={120}
                  height={80}
                  className='w-auto h-auto object-contain max-w-[100px] max-h-[70px] sm:max-w-[120px] sm:max-h-[80px] md:max-w-full md:max-h-none'
                  loading='lazy'
                  sizes='120px'
                />
                )}
              </div>
              )
            })}
          </div>
        )}

        {data?.button?.href && (
          <div
            className={`mt-8 text-center transition-all duration-700 ease-out ${
              sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: sectionVisible ? '300ms' : '0ms' }}
          >
            <Link className='btn' href={data.button.href}>
              {data.button.label}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
